import type { ReactNode } from 'react';
import { CITATION_SOURCES } from '../mock/data.ts';
import type { SourceId } from '../types.ts';

// Inset wrapper for any prose that originated outside vgcbuddy (scrape,
// transcript, social). The boundary must be visually obvious — the user
// has to be able to tell agent reasoning apart from retrieved third-party
// content. See CLAUDE.md untrusted-content principle.
export function UntrustedContent({
  source,
  children,
  variant = 'prose',
}: {
  source: SourceId;
  children: ReactNode;
  variant?: 'prose' | 'quote' | 'table';
}) {
  const meta = CITATION_SOURCES[source];
  return (
    <div
      className="untrusted-inset"
      style={{
        padding: '10px 12px',
        borderRadius: 4,
        borderLeftColor: meta.hue,
        fontStyle: variant === 'quote' ? 'italic' : 'normal',
      }}
    >
      <div
        className="caps mono"
        style={{
          color: 'var(--fg-3)',
          fontSize: 9,
          letterSpacing: '0.08em',
          marginBottom: 6,
          display: 'flex',
          gap: 6,
          alignItems: 'center',
        }}
      >
        <span>retrieved · {meta.label}</span>
        {source.endsWith('-jp') && (
          // Source-language tag. Visually surfaces that this is original
          // Japanese material — it's a feature ("unique data English-only
          // competitors don't have"), not a UX bug. If the user wants
          // English, the agent translates inline; the original quote stays
          // verbatim so attribution is auditable.
          <span
            style={{
              padding: '0 5px',
              background: 'var(--bg-2)',
              border: '1px solid var(--line-1)',
              color: 'var(--fg-2)',
              borderRadius: 2,
              fontSize: 8.5,
              letterSpacing: '0.1em',
            }}
          >
            JA
          </span>
        )}
      </div>
      <div style={{ color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}
